import {
    createContext, Dispatch,
    FC,
    MouseEvent,
    PropsWithChildren, SetStateAction,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import { useSelector } from 'react-redux';
import { equalInLC, toggleInArray } from "@datassential/platform-ui-lib";
import { useDefaultFilterData, useFilters, useMappedFiltersToApi } from "@vms/SelectFilters/SelectFiltersContext";
import { useRestaurantMenu } from "@vms/RestaurantMenu";
import useTopTrendsData, { TopTrendsDataItem, TopTrendsRequest } from "@apiHooks/useTopTrendsData";
import { components } from '@api/api';
import { currentCountrySelector, currentCountryWithPermissionsSelector } from "@selectors/countries.selector";
import { TopTrendsExportRequest } from "@apiHooks/useTopTrendsExport";
import { CategoryItem } from "./TopTrendsControls";
import { FlavorDataItem } from "./TopTrendsTableConfig";
import { selectUpToMax } from "./selectAll";
import { useAiSearchSettingsContext } from "@vms/AiSearchSettingsContext/AiSearchSettingsContext";
import { getTopTrendsApiSortingDirectionNum, TOP_TRENDS_CATEGORIES, TopTrendsSortingColumnsEnum } from "@models/TopTrends";
import { DEFAULT_GROWTH_PERIOD } from "@constants/general";
import { Sort } from "@models";

export type FilterWord = components['schemas']['FilterWord'];
type FilterInput = components['schemas']['MainInputFilter'];

const YEARS = [1, 2, 3, 4, 5, 10];
const TAKE = 20000;
const DEFAULT_INCLUDE_LOW_PENETRATION = false;
const DEFAULT_INCLUDE_LESS_TEN_MENUS = true;

const BASE_CATEGORIES: CategoryItem[] = TOP_TRENDS_CATEGORIES.map(cat => ({
    id: cat.id,
    name: cat.name,
    isSelected: false,
}));

function buildApiRequest(
    showOnlyNewItems: boolean,
    includeLowPenetrationItems: boolean,
    includeLessTenMenusCount: boolean,
    categories: CategoryItem[],
    growthPeriod: number,
    drillWords: FilterWord[],
    skip: number = 0,
    backendFilters?: FilterInput,
    sortingKeyEnumValue?: TopTrendsSortingColumnsEnum | null,
    sortingDirection?: Sort | null,
): TopTrendsRequest {
    const selectedCategories = categories.filter(cat => cat.isSelected);
    const topTrendCategories = selectedCategories.length > 0
        ? selectedCategories.map(cat => ({ id: cat.id, name: cat.name }))
        : categories.map(cat => ({ id: cat.id, name: cat.name }));

    return {
        mainFilter: {
            filters: backendFilters,
            topTrendCategories,
            includeLessOnePercentPenetration: includeLowPenetrationItems,
            includeLessTenMenusCount: includeLessTenMenusCount,
            isNew: showOnlyNewItems,
            yearsGrowth: growthPeriod,
            ...(drillWords.length > 0 ? { drillWords } : {}),
        },
        tableParameters: {
            sortColumn: sortingKeyEnumValue ?? TopTrendsSortingColumnsEnum.Penetration,
            sortDir: getTopTrendsApiSortingDirectionNum(sortingDirection ?? null),
            skip,
            take: TAKE,
        },
    };
}

interface TopTrendsContextType {
    isLoading: boolean;
    tableData: TopTrendsDataItem[];
    flavorData: FlavorDataItem[];
    hasFlavorData: boolean;
    hasFlavorPermission: boolean;
    totalRecords: number;
    exportRequest: TopTrendsExportRequest;
    backendFilters?: FilterInput;

    searchQuery: string;
    showOnlyNewItems: boolean;
    hasNewItemsPermission: boolean;
    includeLowPenetrationItems: boolean;
    includeLessTenMenusCount: boolean;
    categories: CategoryItem[];
    growthPeriod: number;
    drillWords: FilterWord[];
    years: number[];

    isSelectable: boolean;
    selectedWords: string[];
    hasTags: boolean;

    onSearchChange: (value: string) => void;
    onNewItemsToggle: () => void;
    onIncludeOptionsChange: (options: {
        includeLowPenetrationItems: boolean;
        includeLessTenMenusCount: boolean
    }) => void;
    onCategoriesApply: (categoryIds: number[]) => void;
    onCategoriesClear: () => void;
    onGrowthPeriodChange: (period: number) => void;
    onDrillClick: (word: FilterWord) => void;
    onDeleteDrillWord: (wordId: number) => void;
    onReset: () => void;

    onSelectableToggle: () => void;
    onWordSelect: (wordName: string) => void;
    onSelectAll: (visibleWords: string[]) => void;
    onRemoveSelectedWord: (word: string) => void;
    onClearSelections: () => void;

    onSettingsClick: (e: MouseEvent<HTMLDivElement>, word: FilterWord) => void;
    keywordSettingsState: {
        keyword: FilterWord | null;
        isSettingsShown: boolean;
        settingsX: number;
        settingsY: number;
    };
    onCloseKeywordSettings: () => void;

    onMenuExamplesClick: (word: FilterWord) => void;
    menuExamplesWordData: FilterWord | null;
    isMenuExamplesPopupOpened: boolean;
    onCloseMenuExamplesPopup: () => void;

    onCopyContentChange: (content: string) => void;
    copyTableContent: string;
    setSortingKeyEnumValue: Dispatch<SetStateAction<TopTrendsSortingColumnsEnum | null>>;
    setSortingDirection: Dispatch<SetStateAction<Sort | null>>;
}

const TopTrendsContext = createContext<TopTrendsContextType | null>(null);

export const useTopTrends = (): TopTrendsContextType => {
    const context = useContext(TopTrendsContext);
    if (!context) {
        throw new Error('useTopTrends must be used within TopTrendsProvider');
    }
    return context;
};

/** Provides Top Trends data, filters, and keyword-selection state to the page. */
export const TopTrendsProvider: FC<PropsWithChildren<{}>> = ({ children }) => {
    const currentCountry = useSelector(currentCountrySelector);
    const countryCode = useSelector(currentCountrySelector);
    const permissions = useSelector(currentCountryWithPermissionsSelector) as { hasNewItemsOnly?: boolean } | undefined;
    const hasNewItemsPermission = !!permissions?.hasNewItemsOnly;

    const {
        isActive: isAiSearchActive,
        isOnlyAnswer: isAiSearchOnlyAnswer,
        topTrendsSettings,
    } = useAiSearchSettingsContext();
    const [isAiSearchResolved, setIsAiSearchResolved] = useState(isAiSearchOnlyAnswer ? true : !isAiSearchActive);

    const { setActiveRestaurantList } = useFilters();
    const { isLoadingRestaurantLists, rawFilterData } = useRestaurantMenu();
    const backendFilters = useMappedFiltersToApi();
    const defaultFiltersData = useDefaultFilterData();
    const hasTags = (rawFilterData?.tagCategories?.length ?? 0) > 0;

    const {
        data: topTrendsData,
        isLoading: isTopTrendsDataLoading,
        doFetch: refetchTopTrendsData,
    } = useTopTrendsData();

    const [showOnlyNewItems, setShowOnlyNewItems] = useState<boolean>(false);
    const [includeLowPenetrationItems, setIncludeLowPenetrationItems] = useState<boolean>(DEFAULT_INCLUDE_LOW_PENETRATION);
    const [includeLessTenMenusCount, setIncludeLessTenMenusCount] = useState<boolean>(DEFAULT_INCLUDE_LESS_TEN_MENUS);
    const [selectedCategoriesIds, setSelectedCategoriesIds] = useState<number[]>([]);
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [growthPeriod, setGrowthPeriod] = useState<number>(DEFAULT_GROWTH_PERIOD);
    const [drillWords, setDrillWords] = useState<FilterWord[]>([]);
    const [sortingKeyEnumValue, setSortingKeyEnumValue] = useState<TopTrendsSortingColumnsEnum | null>(null);
    const [sortingDirection, setSortingDirection] = useState<Sort | null>(null);

    const categories = useMemo(
        () => {
            if (!selectedCategoriesIds) return BASE_CATEGORIES;
            return BASE_CATEGORIES.map(cat => ({
                ...cat,
                isSelected: selectedCategoriesIds.includes(cat.id),
            }));
        },
        [selectedCategoriesIds]
    );

    useEffect(() => {
        if (topTrendsSettings) {
            const {
                apiFilters,
                topTrendsCategoriesIds,
                includeLessTenMenusCount,
                showOnlyNewItems,
                growthPeriod,
                drillWords,
                includeLessOnePercentPenetration,
            } = topTrendsSettings;

            setSelectedCategoriesIds(topTrendsCategoriesIds);
            setIncludeLessTenMenusCount(includeLessTenMenusCount);
            setIncludeLowPenetrationItems(includeLessOnePercentPenetration);
            setShowOnlyNewItems(showOnlyNewItems);
            if (growthPeriod) {
                setGrowthPeriod(growthPeriod);
            }
            setDrillWords(drillWords);
            const restaurantListId = apiFilters?.restaurantFilters?.restaurantListId ?? 0;
            setActiveRestaurantList(
                restaurantListId,
                apiFilters,
            );

            setIsAiSearchResolved(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [topTrendsSettings]);

    const [accumulatedData, setAccumulatedData] = useState<TopTrendsDataItem[]>([]);
    const [accumulatedFlavorData, setAccumulatedFlavorData] = useState<NonNullable<NonNullable<typeof topTrendsData>['flavorData']>['data']>([]);
    const lastProcessedDataRef = useRef<typeof topTrendsData>(null);

    const [isInitialized, setIsInitialized] = useState(false);

    const [selectedWords, setSelectedWords] = useState<string[]>([]);
    const [isSelectable, setIsSelectable] = useState(false);

    const [keywordSettingsState, setKeywordSettingsState] = useState<{
        keyword: FilterWord | null;
        isSettingsShown: boolean;
        settingsX: number;
        settingsY: number;
    }>({
        keyword: null,
        isSettingsShown: false,
        settingsX: 0,
        settingsY: 0,
    });

    const [isMenuExamplesPopupOpened, setIsMenuExamplesPopupOpened] = useState(false);
    const [menuExamplesWordData, setMenuExamplesWordData] = useState<FilterWord | null>(null);

    const [copyTableContent, setCopyTableContent] = useState<string>('');

    const fetchData = useCallback(
        () => {
            if (!backendFilters || isLoadingRestaurantLists || !isAiSearchResolved) return;

            const request = buildApiRequest(
                showOnlyNewItems,
                includeLowPenetrationItems,
                includeLessTenMenusCount,
                categories,
                growthPeriod,
                drillWords,
                0,
                backendFilters
            );
            refetchTopTrendsData(request);
        },
        [
            showOnlyNewItems,
            includeLowPenetrationItems,
            includeLessTenMenusCount,
            categories,
            growthPeriod,
            drillWords,
            backendFilters,
            refetchTopTrendsData,
        ]
    );

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    useEffect(() => {
        if (!topTrendsData?.data || topTrendsData === lastProcessedDataRef.current) {
            return;
        }
        lastProcessedDataRef.current = topTrendsData;

        setAccumulatedData(topTrendsData.data);
        setAccumulatedFlavorData(topTrendsData.flavorData?.data ?? []);
        setIsInitialized(true);
    }, [topTrendsData]);

    const handleNewItemsToggle = useCallback(
        () => setShowOnlyNewItems(prev => !prev),
        []
    );

    const handleIncludeOptionsChange = useCallback(
        (options: {
            includeLowPenetrationItems: boolean;
            includeLessTenMenusCount: boolean;
        }) => {
            setIncludeLowPenetrationItems(options.includeLowPenetrationItems);
            setIncludeLessTenMenusCount(options.includeLessTenMenusCount);
        },
        []
    );

    const handleCategoriesApply = useCallback(
        (categoryIds: number[]) => {
            setSelectedCategoriesIds(categoryIds);
        },
        []
    );

    const handleCategoriesClear = useCallback(
        () => setSelectedCategoriesIds([]),
        []
    );

    const handleDrillClick = useCallback(
        (word: FilterWord) => {
            setDrillWords(prev => [...prev, word]);
        },
        []
    );

    const handleDeleteDrillWord = useCallback(
        (wordId: number) => {
            setDrillWords(prev => prev.filter(w => w.id !== wordId));
        },
        []
    );

    const handleReset = useCallback(
        () => {
            setIsInitialized(false);
            setShowOnlyNewItems(false);
            setIncludeLowPenetrationItems(DEFAULT_INCLUDE_LOW_PENETRATION);
            setIncludeLessTenMenusCount(DEFAULT_INCLUDE_LESS_TEN_MENUS);
            setSelectedCategoriesIds([]);
            setSearchQuery('');
            setGrowthPeriod(DEFAULT_GROWTH_PERIOD);
            setDrillWords([]);
            setAccumulatedData([]);
            setAccumulatedFlavorData([]);
            lastProcessedDataRef.current = null;
        },
        []
    );

    const handleSelectableToggle = useCallback(
        () => setIsSelectable(prev => !prev),
        []
    );

    /** Toggles one keyword in the Build keyword list selection. */
    const handleWordSelect = useCallback(
        (wordName: string) => setSelectedWords(prev => toggleInArray(prev, wordName)),
        []
    );

    /** Adds the visible words, in order, to the selection until it reaches the select-all max. */
    const handleSelectAll = useCallback(
        (visibleWords: string[]) => setSelectedWords(prev => selectUpToMax(visibleWords, prev)),
        []
    );

    /** Removes one keyword from the selection. */
    const handleRemoveSelectedWord = useCallback(
        (word: string) => setSelectedWords(prev => prev.filter(w => w !== word)),
        []
    );

    const handleClearSelections = useCallback(
        () => setSelectedWords([]),
        []
    );

    useEffect(() => {
        setSearchQuery('');
    }, [drillWords]);

    const handleSettingsClick = useCallback(
        (e: MouseEvent<HTMLDivElement>, word: FilterWord) => {
            const boundingRect = (e.target as HTMLElement).getBoundingClientRect();
            setKeywordSettingsState({
                isSettingsShown: true,
                settingsX: boundingRect.left + 30,
                settingsY: boundingRect.top + boundingRect.height / 2,
                keyword: word,
            });
        },
        []
    );

    const handleCloseKeywordSettings = useCallback(
        () => {
            setKeywordSettingsState(prev => ({
                ...prev,
                isSettingsShown: false,
            }));
        },
        []
    );

    const handleMenuExamplesClick = useCallback(
        (word: FilterWord) => {
            setIsMenuExamplesPopupOpened(true);
            setMenuExamplesWordData(word);
        },
        []
    );

    const handleCloseMenuExamplesPopup = useCallback(
        () => {
            setIsMenuExamplesPopupOpened(false);
        },
        []
    );

    const handleCopyContentChange = useCallback(
        (content: string) => {
            setCopyTableContent(content);
        },
        []
    );

    const searchIndex = useMemo(
        () => accumulatedData.map(item => item.word?.name?.toLowerCase() ?? ''),
        [accumulatedData]
    );

    const tableData = useMemo(
        () => {
            const query = searchQuery.trim();
            if (!query) {
                return accumulatedData;
            }
            const searchLower = query.toLowerCase();
            return accumulatedData.filter((_, index) =>
                searchIndex[index].includes(searchLower)
            );
        },
        [accumulatedData, searchIndex, searchQuery]
    );

    const flavorDataMap = useMemo(
        (): Map<number, FlavorDataItem> => {
            if (!accumulatedFlavorData || accumulatedData.length === 0) {
                return new Map();
            }

            const map = new Map<number, FlavorDataItem>();
            accumulatedData.forEach((item, index) => {
                const flavorItem = accumulatedFlavorData[index];
                if (item.word?.id != null) {
                    map.set(item.word.id, {
                        wordId: item.word.id,
                        knowIt: flavorItem?.knowIt != null ? `${Math.round(flavorItem.knowIt)}%` : '-',
                        haveTriedIt: flavorItem?.haveTriedIt != null ? `${Math.round(flavorItem.haveTriedIt)}%` : '-',
                        loveOrLikeIt: flavorItem?.loveOrLikeIt != null ? `${Math.round(flavorItem.loveOrLikeIt)}%` : '-',
                    });
                }
            });
            return map;
        },
        [accumulatedData, accumulatedFlavorData]
    );

    const flavorData = useMemo(
        () => Array.from(flavorDataMap.values()),
        [flavorDataMap]
    );

    const hasFlavorData = useMemo(
        () => equalInLC(currentCountry, 'USA'),
        [currentCountry]
    );

    const hasFlavorPermission = useMemo(
        () => (topTrendsData?.flavorData?.accessLevel ?? 0) > 0,
        [topTrendsData?.flavorData?.accessLevel]
    );

    const totalRecords = topTrendsData?.totalRecords ?? 0;

    const exportRequest = useMemo(
        (): TopTrendsExportRequest => {
            const currentRequest = buildApiRequest(
                showOnlyNewItems,
                includeLowPenetrationItems,
                includeLessTenMenusCount,
                categories,
                growthPeriod,
                drillWords,
                0,
                backendFilters,
                sortingKeyEnumValue,
                sortingDirection,
            );
            return {
                fullFilter: { ...defaultFiltersData, countryCode } as FilterInput,
                customerFilter: {
                    mainFilter: currentRequest.mainFilter,
                    tableParameters: currentRequest.tableParameters,
                },
            };
        },
        [
            growthPeriod,
            drillWords,
            showOnlyNewItems,
            includeLowPenetrationItems,
            includeLessTenMenusCount,
            categories,
            backendFilters,
            countryCode,
            defaultFiltersData,
            sortingKeyEnumValue,
            sortingDirection,
        ]
    );

    /** Memoized context value exposed through useTopTrends. */
    const value = useMemo<TopTrendsContextType>(
        () => ({
            isLoading: isTopTrendsDataLoading || !isInitialized,
            tableData,
            flavorData,
            hasFlavorData,
            hasFlavorPermission,
            totalRecords,
            exportRequest,
            backendFilters,

            searchQuery: searchQuery,
            showOnlyNewItems,
            hasNewItemsPermission,
            includeLowPenetrationItems,
            includeLessTenMenusCount,
            categories,
            growthPeriod: growthPeriod,
            drillWords: drillWords,
            years: YEARS,

            isSelectable,
            selectedWords,
            hasTags,

            onSearchChange: setSearchQuery,
            onNewItemsToggle: handleNewItemsToggle,
            onIncludeOptionsChange: handleIncludeOptionsChange,
            onCategoriesApply: handleCategoriesApply,
            onCategoriesClear: handleCategoriesClear,
            onGrowthPeriodChange: setGrowthPeriod,
            onDrillClick: handleDrillClick,
            onDeleteDrillWord: handleDeleteDrillWord,
            onReset: handleReset,

            onSelectableToggle: handleSelectableToggle,
            onWordSelect: handleWordSelect,
            onSelectAll: handleSelectAll,
            onRemoveSelectedWord: handleRemoveSelectedWord,
            onClearSelections: handleClearSelections,

            onSettingsClick: handleSettingsClick,
            keywordSettingsState,
            onCloseKeywordSettings: handleCloseKeywordSettings,

            onMenuExamplesClick: handleMenuExamplesClick,
            menuExamplesWordData,
            isMenuExamplesPopupOpened,
            onCloseMenuExamplesPopup: handleCloseMenuExamplesPopup,

            onCopyContentChange: handleCopyContentChange,
            copyTableContent,

            setSortingKeyEnumValue,
            setSortingDirection,
        }),
        [
            isTopTrendsDataLoading,
            isInitialized,
            tableData,
            flavorData,
            hasFlavorData,
            hasFlavorPermission,
            totalRecords,
            exportRequest,
            backendFilters,
            searchQuery,
            showOnlyNewItems,
            hasNewItemsPermission,
            includeLowPenetrationItems,
            includeLessTenMenusCount,
            categories,
            growthPeriod,
            drillWords,
            isSelectable,
            selectedWords,
            hasTags,
            handleNewItemsToggle,
            handleIncludeOptionsChange,
            handleCategoriesApply,
            handleCategoriesClear,
            setGrowthPeriod,
            handleDrillClick,
            handleDeleteDrillWord,
            handleReset,
            handleSelectableToggle,
            handleWordSelect,
            handleSelectAll,
            handleRemoveSelectedWord,
            handleClearSelections,
            handleSettingsClick,
            keywordSettingsState,
            handleCloseKeywordSettings,
            handleMenuExamplesClick,
            menuExamplesWordData,
            isMenuExamplesPopupOpened,
            handleCloseMenuExamplesPopup,
            handleCopyContentChange,
            copyTableContent,
            setSortingKeyEnumValue,
            setSortingDirection,
        ]
    );

    return (
        <TopTrendsContext.Provider value={value}>
            {children}
        </TopTrendsContext.Provider>
    );
};